// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
/**
 * Module Configuration types.
 * Collection: grc_module_config
 * Sprint 4.4.0 -- Platform Settings Consolidation & Module Configuration
 */
/** Identifiers for toggleable modules */
export const ModuleId = {
    RIGHTS_REQUESTS: 'rights_requests',
    DATA_MAPPING: 'data_mapping',
    GOVERNANCE: 'governance',
    CONSENT: 'consent',
    CONSENT_PROPERTIES: 'consent_properties',
    // Sprint 40.2.0 - Data Subject Profiles (part of Light tier, positioned after Consent Properties)
    DATA_SUBJECTS: 'data_subjects',
    ASSESSMENTS: 'assessments',
    CLASSIFICATION: 'classification',
    MONITORING: 'monitoring',
    INCIDENTS: 'incidents',
    CONNECTORS: 'connectors',
    VENDOR_RISK: 'vendor_risk',
    DELETION_ORCHESTRATION: 'deletion_orchestration',
    DISCOVERY: 'discovery',
    INCIDENT_OPS: 'incident_ops',
    PROGRAM_GOVERNANCE: 'program_governance',
    ACCESS_ACCOUNTABILITY: 'access_accountability',
    LOCALIZATION: 'localization',
    PARTNER_WORKSPACE: 'partner_workspace',
    PRIVACY_SCANNER: 'privacy_scanner',
    ONBOARDING: 'onboarding',
    // Sprint 15.5.0 - Add missing sidebar modules
    COMPLIANCE_FORMS: 'compliance_forms',
    AUDIT_TRAIL: 'audit_trail',
    ORGANIZATIONS: 'organizations',
    // Sprint 17.4.0 - Task Management
    TASK_MANAGEMENT: 'task_management',
    // Sprint 31.x - Help Center
    HELP_CENTER: 'help_center',
};
/** All module IDs as an array for iteration */
export const ALL_MODULE_IDS = Object.values(ModuleId);
/** Human-readable labels for modules (keyed by ModuleId) */
export const MODULE_DISPLAY_NAMES = {
    [ModuleId.RIGHTS_REQUESTS]: { en: 'Rights Requests', vi: 'Y\u00eau c\u1ea7u quy\u1ec1n' },
    [ModuleId.DATA_MAPPING]: { en: 'Data Mapping', vi: 'B\u1ea3n \u0111\u1ed3 d\u1eef li\u1ec7u' },
    [ModuleId.GOVERNANCE]: {
        en: 'Vendor Governance',
        vi: 'Qu\u1ea3n tr\u1ecb nh\u00e0 cung c\u1ea5p',
    },
    [ModuleId.CONSENT]: {
        en: 'Consent Governance',
        vi: 'Qu\u1ea3n tr\u1ecb \u0111\u1ed3ng thu\u1eadn',
    },
    [ModuleId.CONSENT_PROPERTIES]: {
        en: 'Consent Properties',
        vi: 'Thu\u1ed9c t\u00ednh \u0111\u1ed3ng thu\u1eadn',
    },
    [ModuleId.ASSESSMENTS]: { en: 'DPIA & Assessments', vi: '\u0110\u00e1nh gi\u00e1 DPIA' },
    [ModuleId.CLASSIFICATION]: {
        en: 'Data Classification',
        vi: 'Ph\u00e2n lo\u1ea1i d\u1eef li\u1ec7u',
    },
    [ModuleId.MONITORING]: {
        en: 'Monitoring Programs',
        vi: 'Ch\u01b0\u01a1ng tr\u00ecnh gi\u00e1m s\u00e1t',
    },
    [ModuleId.INCIDENTS]: { en: 'Incident Response', vi: '\u1ee8ng ph\u00f3 s\u1ef1 c\u1ed1' },
    [ModuleId.CONNECTORS]: { en: 'Connectors', vi: 'K\u1ebft n\u1ed1i' },
    [ModuleId.VENDOR_RISK]: { en: 'Vendor Risk', vi: 'R\u1ee7i ro nh\u00e0 cung c\u1ea5p' },
    [ModuleId.DELETION_ORCHESTRATION]: {
        en: 'Deletion Orchestration',
        vi: '\u0110i\u1ec1u ph\u1ed1i x\u00f3a',
    },
    [ModuleId.DISCOVERY]: { en: 'Data Discovery', vi: 'Kh\u00e1m ph\u00e1 d\u1eef li\u1ec7u' },
    [ModuleId.INCIDENT_OPS]: { en: 'Incident Operations', vi: 'V\u1eadn h\u00e0nh s\u1ef1 c\u1ed1' },
    [ModuleId.PROGRAM_GOVERNANCE]: {
        en: 'Program Governance',
        vi: 'Qu\u1ea3n tr\u1ecb ch\u01b0\u01a1ng tr\u00ecnh',
    },
    [ModuleId.ACCESS_ACCOUNTABILITY]: {
        en: 'Access Accountability',
        vi: 'Tr\u00e1ch nhi\u1ec7m truy c\u1eadp',
    },
    [ModuleId.LOCALIZATION]: { en: 'Localization', vi: 'B\u1ea3n \u0111\u1ecba h\u00f3a' },
    [ModuleId.PARTNER_WORKSPACE]: {
        en: 'Partner Workspace',
        vi: 'Kh\u00f4ng gian \u0111\u1ed1i t\u00e1c',
    },
    [ModuleId.PRIVACY_SCANNER]: {
        en: 'Privacy Scanner',
        vi: 'Qu\u00e9t quy\u1ec1n ri\u00eang t\u01b0',
    },
    [ModuleId.ONBOARDING]: { en: 'Onboarding', vi: 'Kh\u1edfi \u0111\u1ed9ng' },
    // Sprint 15.5.0 - Add missing sidebar modules
    [ModuleId.COMPLIANCE_FORMS]: {
        en: 'Compliance Forms',
        vi: 'Bi\u1ec3u m\u1eabu tu\u00e2n th\u1ee7',
    },
    [ModuleId.AUDIT_TRAIL]: { en: 'Audit Trail', vi: 'Nh\u1eadt k\u00fd ki\u1ec3m to\u00e1n' },
    [ModuleId.ORGANIZATIONS]: { en: 'Organizations', vi: 'T\u1ed5 ch\u1ee9c' },
    // Sprint 17.4.0
    [ModuleId.TASK_MANAGEMENT]: {
        en: 'Task Management',
        vi: 'Qu\u1ea3n l\u00fd c\u00f4ng vi\u1ec7c',
    },
    // Sprint 31.x
    [ModuleId.HELP_CENTER]: {
        en: 'Help Center',
        vi: 'Trung t\u00e2m tr\u1ee3 gi\u00fap',
    },
    // Sprint 40.2.0
    [ModuleId.DATA_SUBJECTS]: {
        en: 'Data Subjects',
        vi: 'Ch\u1ee7 th\u1ec3 d\u1eef li\u1ec7u',
    },
};
/** Build a default config with all modules enabled */
export function buildDefaultModuleConfig() {
    const modules = {};
    for (const id of ALL_MODULE_IDS) {
        modules[id] = { enabled: true };
    }
    return modules;
}
//# sourceMappingURL=module-config.js.map